import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";

interface WarehouseOption {
  warehouse_id: string | number;
  warehouse_name: string;
  warehouse_type?: string;
  [key: string]: any;
}

interface UseWarehouseSelectionOptions {
  sessionStorageKey?: string; // Custom sessionStorage key (default: "WarehouseIdDispatch")
  autoSelectFirst?: boolean; // Auto-select first warehouse if none selected
  onWarehouseChange?: (warehouseId: string | null, warehouse: WarehouseOption | null) => void;
}

interface UseWarehouseSelectionReturn {
  selectedWarehouseId: string | null;
  selectedWarehouse: WarehouseOption | null;
  warehouseOptions: WarehouseOption[];
  loading: boolean;
  setSelectedWarehouse: (warehouseId: string | null, warehouse?: WarehouseOption | null) => void;
  getWarehouseById: (warehouseId: string | number) => WarehouseOption | null;
  refreshWarehouses: () => Promise<void>;
}

/**
 * Custom hook for managing warehouse selection using sessionStorage
 * Replaces the removed warehouse context with a sessionStorage-based solution
 */
export const useWarehouseSelection = (
  options: UseWarehouseSelectionOptions = {}
): UseWarehouseSelectionReturn => {
  const {
    sessionStorageKey = "WarehouseIdDispatch",
    autoSelectFirst = true,
    onWarehouseChange,
  } = options;

  const dispatch = useDispatch<any>();
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWarehouseId, setSelectedWarehouseIdState] = useState<string | null>(null);
  const [selectedWarehouse, setSelectedWarehouseState] = useState<WarehouseOption | null>(null);

  // Fetch warehouse options
  const fetchWarehouses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchWarehouseDropdown());
      const warehouses = response?.payload?.data || response?.payload || [];
      setWarehouseOptions(warehouses);

      // If no warehouse is selected, try to get from sessionStorage or auto-select first
      const storedWarehouseId = sessionStorage.getItem(sessionStorageKey);
      
      if (storedWarehouseId && warehouses.length > 0) {
        const warehouse = warehouses.find(
          (w: WarehouseOption) => String(w.warehouse_id) === String(storedWarehouseId)
        );
        if (warehouse) {
          setSelectedWarehouseIdState(String(storedWarehouseId));
          setSelectedWarehouseState(warehouse);
          onWarehouseChange?.(String(storedWarehouseId), warehouse);
        } else if (autoSelectFirst && warehouses.length > 0) {
          // Stored warehouse not found, use first available
          const firstWarehouse = warehouses[0];
          const firstWarehouseId = String(firstWarehouse.warehouse_id);
          setSelectedWarehouseIdState(firstWarehouseId);
          setSelectedWarehouseState(firstWarehouse);
          sessionStorage.setItem(sessionStorageKey, firstWarehouseId);
          onWarehouseChange?.(firstWarehouseId, firstWarehouse);
        }
      } else if (autoSelectFirst && warehouses.length > 0) {
        // No stored warehouse, auto-select first
        const firstWarehouse = warehouses[0];
        const firstWarehouseId = String(firstWarehouse.warehouse_id);
        setSelectedWarehouseIdState(firstWarehouseId);
        setSelectedWarehouseState(firstWarehouse);
        sessionStorage.setItem(sessionStorageKey, firstWarehouseId);
        onWarehouseChange?.(firstWarehouseId, firstWarehouse);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, sessionStorageKey, autoSelectFirst, onWarehouseChange]);

  // Initial fetch
  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // Set selected warehouse
  const setSelectedWarehouse = useCallback(
    (warehouseId: string | null, warehouse?: WarehouseOption | null) => {
      if (warehouseId) {
        // Find warehouse if not provided
        const warehouseToSet =
          warehouse ||
          warehouseOptions.find(
            (w) => String(w.warehouse_id) === String(warehouseId)
          ) ||
          null;

        if (warehouseToSet) {
          setSelectedWarehouseIdState(String(warehouseId));
          setSelectedWarehouseState(warehouseToSet);
          sessionStorage.setItem(sessionStorageKey, String(warehouseId));
          onWarehouseChange?.(String(warehouseId), warehouseToSet);
        }
      } else {
        // Clear selection
        setSelectedWarehouseIdState(null);
        setSelectedWarehouseState(null);
        sessionStorage.removeItem(sessionStorageKey);
        onWarehouseChange?.(null, null);
      }
    },
    [warehouseOptions, sessionStorageKey, onWarehouseChange]
  );

  // Get warehouse by ID
  const getWarehouseById = useCallback(
    (warehouseId: string | number): WarehouseOption | null => {
      return (
        warehouseOptions.find(
          (w) => String(w.warehouse_id) === String(warehouseId)
        ) || null
      );
    },
    [warehouseOptions]
  );

  return {
    selectedWarehouseId,
    selectedWarehouse,
    warehouseOptions,
    loading,
    setSelectedWarehouse,
    getWarehouseById,
    refreshWarehouses: fetchWarehouses,
  };
};

